import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {ActivityLine, PersonMultipleLine} from '@dropbox/dig-icons/assets';
import DropboxIcon from 'assets/DropboxIcon.svg';
import {isMobileAtom} from 'atoms/layout';
import {snackbarAtom} from 'atoms/snackbar';
import cx from 'classnames';
import {TeamInfo, TeamInfoFull, TeamWithHierarchy} from 'client';
import {Breadcrumb} from 'components/DSYS/Breadcrumb';
import {Layout} from 'components/DSYS/Layout';
import {DSYS_RAIL_INSET_LEFT_PX, DSYS_RAIL_INSET_RIGHT_PX} from 'components/DSYS/layoutConstants';
import {Link} from 'components/DSYS/Link';
import type {RailTreeNode} from 'components/DSYS/RailTree';
import {Title} from 'components/DSYS/Title';
import {useTeam, useTeamEdit, useTeamsCount, useTeamsTreeSuspense} from 'components/teams/hooks';
import {NewTeamButton} from 'components/teams/NewTeamButton';
import {TeamAuditLog} from 'components/teams/TeamAuditLog';
import {TeamBody} from 'components/teams/TeamBody';
import {sortEmployeesPrimaryFirst, TeamMemberTypeahead} from 'components/teams/TeamModify';
import {TeamsPage} from 'components/teams/TeamsPage';
import {TeamsSectionTabs} from 'components/teams/TeamsSectionTabs';
import {getDefaultTeamsRoute} from 'constant';
import {sortEmployees} from 'helpers/utils';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {Suspense, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';

function flattenTree(tree: TeamWithHierarchy[]) {
  let result: TeamWithHierarchy[] = [];

  tree.forEach((node) => {
    result.push(node);
    if (node.children) {
      result = result.concat(flattenTree(node.children));
    }
  });

  return result;
}

function findPathToTeam(
  teams: TeamWithHierarchy[],
  targetTeamId: string
): TeamWithHierarchy[] | null {
  for (const team of teams) {
    if (team.team_id === targetTeamId) {
      return [team];
    }

    if (team.children) {
      const path = findPathToTeam(team.children, targetTeamId);
      if (path) {
        return [team, ...path];
      }
    }
  }

  return null;
}

const toRailNodes = (teams: TeamWithHierarchy[]): RailTreeNode<TeamWithHierarchy>[] =>
  teams.map((team) => ({
    id: team.team_id,
    label: team.name ?? '',
    href: `/teams/${team.slug}`,
    children: toRailNodes(team.children ?? []),
    data: team,
  }));

// The team tree, or (while searching) the first 20 matches as a flat list.
const TeamNav = ({
  team,
  search,
  setSearch,
  bleed,
}: {
  team: TeamInfo;
  search: string;
  setSearch: (search: string) => void;
  // Full-bleed rows in the page rail; rounded rows in the narrow screens' nav.
  bleed?: {left: number; right: number};
}) => {
  const teams = useTeamsTreeSuspense();
  const navigate = useNavigate();
  // The clicked team, highlighted at once: the route's loader fetches the team
  // before the page (and `team`) changes, so the rail would otherwise lag.
  const [pendingId, setPendingId] = useState<string>();
  useEffect(() => setPendingId(undefined), [team.team_id]);
  const selectedId = pendingId ?? team.team_id;
  const open = (node: RailTreeNode<TeamWithHierarchy>) => {
    if (node.id !== team.team_id) setPendingId(String(node.id));
    navigate(node.href!);
  };
  const nodes = useMemo(() => toRailNodes(teams.children ?? []), [teams]);
  const matches = useMemo(() => {
    const query = search.toLowerCase();
    return flattenTree(teams.children ?? [])
      .filter(({name}) => name?.toLowerCase().includes(query))
      .slice(0, 20)
      .map((match) => ({...toRailNodes([match])[0], children: []}));
  }, [teams, search]);

  return search.length ? (
    <Layout.RailTree
      nodes={matches}
      selectedId={selectedId}
      onSelect={(node) => {
        setSearch('');
        open(node);
      }}
      bleed={bleed}
    />
  ) : (
    <Layout.RailTree
      nodes={nodes}
      selectedId={selectedId}
      onSelect={open}
      bleed={bleed}
      // Org-level teams bold in normal text (not gray: there are ~20 and each is a
      // destination), sub-teams regular; the open team's org is tinted.
      groupRoots
      groupTone="standard"
      tintSelectedBranch
    />
  );
};

const TotalTeamCounts = ({employees}: {employees: number}) => {
  return (
    <Box style={{minHeight: '20px'}}>
      <Text size="small" color="faint">
        {t('people_count_formatted', {count: employees, countString: employees.toLocaleString()})}
      </Text>
    </Box>
  );
};

// The tree's rows bleed back across the page rail's insets, so fills run edge to edge.
const RAIL_BLEED = {left: DSYS_RAIL_INSET_LEFT_PX, right: DSYS_RAIL_INSET_RIGHT_PX};

export const TeamsView = ({team: {slug}}: {team: TeamInfoFull}) => {
  const team = useTeam({slug: slug})!;
  const {isLoading, data} = useTeamsCount();

  const [search, setSearch] = useState<string>('');
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState<'audit' | 'members'>();
  const [members, setMembers] = useState(team.employees.sort(sortEmployees));
  const [primaryContact, setPrimaryContact] = useState(team.primary_contact ?? undefined);
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const navRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState(false);

  const {editTeam, isPending} = useTeamEdit();

  useEffect(() => {
    if (!isDrawerOpen) {
      setTimeout(() => {
        setDrawerType(undefined);
      }, 300);
    }
  }, [isDrawerOpen]);

  useEffect(() => {
    setMembers(team.employees);
    setPrimaryContact(team.primary_contact ?? undefined);

    setDrawerOpen(false);
    setDrawerType(undefined);
  }, [team.employees, team.primary_contact, team.slug]);

  // const onClick = (path: string) => {
  //   analyticsLogger().logEvent('TEAMS_BREADCRUMB_CLICKED', {path});
  // };

  useEffect(() => {
    if (!navRef.current || !navRef.current.parentElement || !navRef.current.style) {
      return;
    }

    navRef.current.style.width = `${navRef.current.parentElement.offsetWidth}px`;
    window.addEventListener('resize', () => {
      if (!navRef.current) {
        return;
      }

      navRef.current.style.width = `${navRef.current.parentElement!.offsetWidth}px`;
    });
  }, []);

  const navClassName = cx(expanded && atoms({display: 'none'}));
  const isMobile = useAtomValue(isMobileAtom);

  // The directory's top: the company root.
  const railHeader = (
    <Link
      to={getDefaultTeamsRoute()}
      hasNoUnderline
      onClick={() => setSearch('')}
      className={atoms({
        display: 'flex',
        alignItems: 'center',
        marginBottom: '4',
        marginX: '4',
      })}
    >
      <img src={DropboxIcon} width="40" />
      <Box marginLeft="12" width="100%">
        {isLoading ? (
          <>
            <Skeleton.Text width={100} />
            <Skeleton.Text width={50} />
          </>
        ) : (
          <>
            <Text size="large" tagName="div" isBold>
              {data?.rootTeamName}
            </Text>
            <TotalTeamCounts employees={data?.employees ?? 0} />
          </>
        )}
      </Box>
    </Link>
  );

  // Filters the tree as you type; a rail row, like Workstreams' search.
  const railSearch = (bleed?: {left: number; right: number}) => (
    <Layout.RailSearchRow
      value={search}
      onChange={setSearch}
      placeholder={t('select_teams').toString()}
      bleed={bleed}
    />
  );

  // The team tree (or search matches).
  const railTree = (bleed?: {left: number; right: number}) => (
    <Suspense
      fallback={
        <Box display="flex" flexDirection="row" alignItems="center" padding="4" margin="4">
          <Skeleton.Avatar size="small" />
          <Box marginLeft="8" width="100%">
            <Skeleton.Text width={200} />
          </Box>
        </Box>
      }
    >
      <TeamNav team={team} search={search} setSearch={setSearch} bleed={bleed} />
    </Suspense>
  );

  // Page rail: the header block one row inset in; the tree directly in the rail,
  // full-bleed, so team names sit on the gutter under "Teams".
  const railContent = (
    <>
      <Box paddingX="8" paddingTop="16">
        {railHeader}
      </Box>
      <Box paddingTop="12">{railSearch(RAIL_BLEED)}</Box>
      <Box paddingTop="4" paddingBottom="16">
        {railTree(RAIL_BLEED)}
      </Box>
    </>
  );

  const breadcrumb = (
    <Suspense fallback={<Breadcrumb path={[{children: 'Teams', to: getDefaultTeamsRoute()}]} />}>
      <TeamBreadcrumb team={team} />
    </Suspense>
  );

  const body = (
    <TeamBody
      expanded={expanded}
      setExpanded={setExpanded}
      team={team}
      handleOpenDrawer={(type) => {
        setDrawerOpen(true);
        setDrawerType(type);
      }}
    />
  );

  // The audit log / members editor, sliding in from the right.
  const drawerProps = {
    open: Boolean(isDrawerOpen),
    drawerHeader: (
      <Title size={18}>{drawerType === 'audit' ? t('audit_log_title') : t('members_title')}</Title>
    ),
    drawerIcon: drawerType === 'audit' ? ActivityLine : PersonMultipleLine,
    drawerBody:
      drawerType === 'audit' ? (
        <TeamAuditLog slug={team.slug} />
      ) : drawerType === 'members' ? (
        <TeamMemberTypeahead
          initialSelections={members.sort(sortEmployeesPrimaryFirst(primaryContact?.ldap))}
          initialPrimaryContact={primaryContact}
          isPending={isPending}
          onBeforeSave={async (selections, contact) => {
            try {
              await editTeam({
                teamId: team?.slug ?? '',
                data: {
                  team_id: team?.team_id ?? '',
                  parent_team: team?.parent?.team_id,
                  primary_contact: contact?.ldap,
                  employees: selections.map(({user_id}) => user_id),
                  name: team.name,
                  description: team.description,
                },
              });

              setSnackbarMessage({text: t('saved')});
            } catch {
              setSnackbarMessage({text: t('error_saving')});
            }
          }}
          onSave={(selections, primary) => {
            setPrimaryContact(primary);
            setMembers(selections);
            setDrawerOpen(false);
          }}
          onCancel={() => setDrawerOpen(false)}
        />
      ) : null,
    onClose: () => setDrawerOpen(false),
  };

  if (!isMobile) {
    return (
      <>
        {/* The expanded org chart takes the rail's room too. */}
        {/* No breadcrumb: the rail's tree already shows where the team sits. */}
        <TeamsPage
          active="teams"
          rail={expanded ? undefined : railContent}
          action={<NewTeamButton parentSlug={team.slug} />}
        >
          {body}
        </TeamsPage>
        {/* With no children, only the drawer renders (it's fixed to the viewport). */}
        <Layout.InlineDrawerContainer {...drawerProps} />
      </>
    );
  }

  return (
    <Layout.InlineDrawerContainer {...drawerProps}>
      <Layout.Nav className={navClassName}>
        <Stack gap="12">
          <TeamsSectionTabs active="teams" />
          {railHeader}
          {railSearch()}
          {railTree()}
        </Stack>
      </Layout.Nav>
      <Layout.Body breadcrumb={breadcrumb}>{body}</Layout.Body>
    </Layout.InlineDrawerContainer>
  );
};

export const TeamBreadcrumb = ({team}: {team: TeamInfoFull}) => {
  const teams = useTeamsTreeSuspense();

  // find team in teams
  const selectedTeamInTeams = useMemo(() => {
    return findPathToTeam(teams.children ?? [], team.team_id);
  }, [teams, team]);

  return (
    <Breadcrumb
      path={[
        {children: 'Teams', to: getDefaultTeamsRoute()},
        ...(selectedTeamInTeams ?? []).map(({name, slug}) => ({
          children: name!,
          to: `/teams/${slug}`,
        })),
      ]}
    />
  );
};

// const prefixBreadcrumbs = [
//   {name: t('teams'), path: '/teams', isBold: true},
//   {name: 'Dropbox', path: '/teams'},
// ];

// const formatDashboardsBreadcrumbs = (teamHierarchy: Team[]): BreadcrumbData[] => {
//   return teamHierarchy.map((team) => ({
//     name: team.name!,
//     path: `/teams/${team.slug}`,
//   }));
// };
